/**
 * Utility functions for handling tags in Product Wiki components
 */

export interface Tag {
  text?: string; // For backward compatibility
  status?: string; // For ANDA status
  applicant?: string; // For ANDA applicant
  earliest_possible_launch_date?: string; // For LOE launch date
  earliest_launch_probability?: string; // For LOE launch probability
  first_anda_filers?: string; // For LOE first filers
  other_anda_filers?: string; // For LOE other filers
  type: 'urgent' | 'medium' | 'normal';
}

/**
 * Extracts display text from a tag object
 * Supports both old format (text) and new format (status, applicant, LOE fields)
 *
 * @param tag - The tag object
 * @param truncate - Whether to truncate long values (default: true)
 * @returns The display text for the tag
 */
export const getTagText = (tag: Tag, truncate: boolean = true): string => {
  if (tag.text) return tag.text;
  if (tag.status) return tag.status;
  if (tag.applicant) return tag.applicant;
  if (tag.earliest_possible_launch_date) return tag.earliest_possible_launch_date;
  if (tag.earliest_launch_probability) return tag.earliest_launch_probability;
  if (tag.first_anda_filers) {
    const value = tag.first_anda_filers;
    return truncate && value.length > 30
      ? 'First ANDA Filers: ' + value.substring(0, 30) + '...'
      : 'First ANDA Filers: ' + value;
  }
  if (tag.other_anda_filers) {
    const value = tag.other_anda_filers;
    return truncate && value.length > 30
      ? 'Other ANDA Filers: ' + value.substring(0, 30) + '...'
      : 'Other ANDA Filers: ' + value;
  }
  return 'N/A';
};

/**
 * Formats tags for display in table cells
 *
 * @param tags - Array of tag objects
 * @returns Comma-separated string of tag texts
 */
export const formatTagsForTable = (tags?: Tag[]): string => {
  if (!tags || tags.length === 0) return '-';
  return tags
    .map(tag => getTagText(tag))
    .filter(text => text)
    .join(', ');
};

/**
 * Gets the first tag text (useful for single tag display)
 *
 * @param tags - Array of tag objects
 * @returns The text of the first tag or empty string
 */
export const getFirstTagText = (tags?: Tag[]): string => {
  if (!tags || tags.length === 0) return '';
  return getTagText(tags[0]);
};

/**
 * Gets tag by type (status, applicant)
 *
 * @param tags - Array of tag objects
 * @param type - The type of tag to find ('status', 'applicant')
 * @returns The tag text or undefined
 */
export const getTagByType = (
  tags: Tag[] | undefined,
  type: 'status' | 'applicant'
): string | undefined => {
  if (!tags || tags.length === 0) return undefined;

  const tag = tags.find(t => t[type] !== undefined);
  return tag ? tag[type] || undefined : undefined;
};
